import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { TokensResponse, User } from "../types/users/TokensResponse";
import { AuthHandler } from "../services/communications/authHandler";
import { useAppStore } from "../stores/useAppStore";
import { UserType } from "../types/users/userTypes";
import { removeStorage, setStorageToken } from "../utils/token.store";
import { getBaseUrl } from "../configurations/environmentVariables";

// Query keys
export const authQueryKeys = {
    tokens: (idToken?: string) => ["auth", "tokens", idToken] as const
};

export const useAuth = () => {

    const authHandler = new AuthHandler();
    const { user, login, logout } = useAppStore();
    const { accounts, instance, inProgress } = useMsal();

    // Usar accounts en lugar de result para obtener el idToken después del reload
    const account = accounts[0]; // Primer account disponible

    const tokensQuery = useQuery<TokensResponse | null>({
        queryKey: authQueryKeys.tokens(account?.homeAccountId),
        queryFn: async () => {
            if (!account) {
                throw new Error("No hay cuenta autenticada");
            }
            const response = await instance.acquireTokenSilent({
                account: account,
                scopes: ["openid", "profile"]
            });
            return authHandler.getTokensFromB2C(response.idToken);
        },
        enabled: !!account,
        staleTime: 1000 * 60 * 5,     // 5 minutos
        gcTime: 1000 * 60 * 10,       // 10 minutos
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true
    });

    useEffect(() => {
        if (!account && inProgress === InteractionStatus.None) {
            console.warn("[AUTH] No account → redirect to B2C");
            instance.acquireTokenRedirect({
                scopes: ["openid", "profile"],
            });
        }
    }, [account, inProgress, instance]);

    useEffect(() => {
        if (tokensQuery.isSuccess && tokensQuery.data) {
            const { usuarioId, token, otp, nombre, email, numPC, ...rest } = tokensQuery.data;

            const userToLogin: UserType = {
                name: nombre,
                email: email,
                numPC: numPC,
                roles: rest.roles,
                isAuthorized: true,
                isAuthenticated: true
            };

            setStorageToken(token, usuarioId);
            login(userToLogin);
        }
    }, [tokensQuery.isSuccess, tokensQuery.data, login]);

    useEffect(() => {
        if (tokensQuery.isError && !tokensQuery.isFetching) {
            const error = tokensQuery.error;

            if (error instanceof InteractionRequiredAuthError) {
                console.warn("[AUTH] MSAL interaction required → redirect to B2C");
                instance.acquireTokenRedirect({
                    scopes: ["openid", "profile"],
                });
                return;
            }

            // Error NO relacionado con MSAL (backend, red, 500, etc.)
            console.error("[AUTH] Backend or network error:", error);
        }
    }, [tokensQuery.isError, tokensQuery.isFetching, instance]);

    const setLogout = async () => {
        logout(); // Limpia el estado local
        removeStorage(); // Limpia localStorage

        if (instance) {
            await instance.logoutRedirect({
                postLogoutRedirectUri: getBaseUrl()
            });
        }
    }

    if (!account) {
        return {
            user,
            instance,
            hasIdToken: false,
            isLoading: false,
            isError: true,
            error: new Error("No se ha podido recuperar el Token de B2C"),
            logout: setLogout
        };
    }

    return {
        user,
        instance,
        hasIdToken: true,
        isLoading: tokensQuery.isLoading,
        isError: tokensQuery.isError,
        error: tokensQuery.error,
        logout: setLogout
    };
}